type TTLogoProps = {
  collapsed?: boolean;
  theme?: 'light' | 'dark';
  variant?: 'horizontalSplit' | 'horizontalLockup' | 'stacked' | 'symbol';
  /** Tenant sidebar logo for the expanded rail. Falls back to the runtime brand asset when null. */
  expandedLogoHref?: string | null;
  /** Tenant compact / logo-mark for the collapsed rail. Falls back to the runtime symbol when null. */
  collapsedLogoHref?: string | null;
  collapsedLogoScale?: number;
  expandedLogoOffsetY?: number;
  expandedLogoScale?: number;
  expandedLogoCentered?: boolean;
  textLogo?: string | null;
  textLogoInitials?: string | null;
};

import { useRef, useEffect } from 'react';
import { getBranding } from '../../config/branding';
import { currentRuntime } from '../../config/runtime';
import { getAppLogoSrc, getAppLogoSizeStyle } from '../../constants/branding';

export function TTLogo({ collapsed = false, theme = 'light', variant, expandedLogoHref = null, collapsedLogoHref = null, collapsedLogoScale = 1, expandedLogoOffsetY = 0, expandedLogoScale = 1, expandedLogoCentered = false, textLogo = null, textLogoInitials = null }: TTLogoProps) {
  const expandedVariant = variant ?? 'stacked';
  // Collapsed sidebar should show the compact symbol glyph; only the expanded banner uses the
  // provided `variant`. This prevents passing `variant="stacked"` from also being used for
  // the collapsed square image.
  const collapsedVariant = 'symbol';

  const expandedSrc = expandedLogoHref || getAppLogoSrc({ variant: expandedVariant, theme });
  const collapsedSrc = collapsedLogoHref || getAppLogoSrc({ variant: collapsedVariant, theme });
  const expandedSizeStyle = getAppLogoSizeStyle({ variant: expandedVariant });
  const collapsedSizeStyle = getAppLogoSizeStyle({ variant: collapsedVariant });

  const branding = getBranding(currentRuntime.brand.id);

  // Keep the sidebar logo compact so the stacked mark does not dominate the rail.
  // Use a fixed slot height for the collapsed sidebar so spacing between the
  // logo and the toggle button remains constant regardless of the artwork size.
  const COLLAPSED_SLOT_HEIGHT = 64; // px
  const wrapperStyle: React.CSSProperties = collapsed
    ? { width: '100%', height: COLLAPSED_SLOT_HEIGHT }
    : { width: '100%', height: expandedSizeStyle.height || 64 };

  const expandedRef = useRef<HTMLImageElement | null>(null);
  const collapsedRef = useRef<HTMLImageElement | null>(null);

  // Preload and decode the image that will become visible to avoid decode blink.
  useEffect(() => {
    const targetSrc = collapsed ? collapsedSrc : expandedSrc;
    const img = new Image();
    img.src = targetSrc;
    // attempt decode if supported
    if ((img as any).decode) {
      (img as any).decode().catch(() => {}).then(() => {
        // small timeout to allow browser to attach to layer
        setTimeout(() => {
          // no-op: ensure image is available in cache/decoded
        }, 50);
      });
    }
    return () => { /* no cleanup needed */ };
  }, [collapsed, expandedSrc, collapsedSrc]);

  // Dev-only logging to verify which assets are being resolved at runtime.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    let isDev = false;
    try {
      isDev = Boolean((import.meta as any).env?.DEV);
    } catch {
      isDev = false;
    }
    if (!isDev) return;

    // Log resolved sources and sizing so you can confirm the exact file being used.
    // This will appear in the browser console when running the dev server.
    // Keep this lightweight and safe for production (guarded by Vite's DEV flag).
    console.info('TTLogo resolved', {
      tenant: branding.tenantId,
      expandedVariant,
      expandedSrc,
      collapsedVariant,
      collapsedSrc,
      expandedSizeStyle,
      collapsedSizeStyle,
      collapsed,
    });
  }, [expandedSrc, collapsedSrc, expandedVariant, collapsedVariant, expandedSizeStyle, collapsedSizeStyle, collapsed]);

  return (
    <div
      className="relative select-none min-w-0 overflow-visible"
      style={wrapperStyle}
    >
      {textLogo ? (
        collapsed ? (
          <span id="tt-logo-collapsed" className={`absolute inset-0 flex items-center justify-center px-1 text-center text-lg font-bold tracking-wide ${theme === 'dark' ? 'text-white' : 'text-slate-800'}`}>{textLogoInitials || textLogo.slice(0, 3).toUpperCase()}</span>
        ) : (
          <span id="tt-logo-expanded" className={`absolute inset-0 flex items-center justify-center px-2 text-center text-base font-semibold leading-tight tracking-wide ${theme === 'dark' ? 'text-white' : 'text-slate-800'}`}>{textLogo}</span>
        )
      ) : collapsed ? (
        // When the sidebar is collapsed we only render the compact `symbol` glyph. This
        // avoids any chance of the horizontal/stacked asset being visible in the rail.
        // Tenant compact marks render at their configured size, with only explicit
        // tenant tuning applied; the built-in symbol is enlarged (1.5x) to fill the rail slot.
        <img
          ref={collapsedRef}
          src={collapsedSrc}
          alt={branding.companyName || branding.appName || 'your workspace'}
          id="tt-logo-collapsed"
          className={`absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 opacity-100`}
          style={{
            width: collapsedSizeStyle.width || 80,
            height: collapsedSizeStyle.height || 80,
            objectFit: 'contain',
            objectPosition: 'center',
            transform: collapsedLogoHref && collapsedLogoScale !== 1
              ? `translate(-50%, -50%) scale(${collapsedLogoScale})`
              : collapsedLogoHref
                ? 'translate(-50%, -50%)'
              : 'translate(-50%, -50%) scale(1.5)',
          }}
          loading="eager"
          decoding="sync"
          fetchPriority="high"
          aria-hidden={!collapsed}
        />
      ) : (
        // Expanded banner logo (fills wrapper, left-aligned)
        <img
          ref={expandedRef}
          src={expandedSrc}
          alt={branding.companyName || branding.appName || 'your workspace'}
          id="tt-logo-expanded"
          className={`absolute left-0 top-0 h-full w-full max-h-full max-w-full opacity-100`}
          style={{ top: expandedLogoOffsetY ? `${expandedLogoOffsetY}px` : undefined, objectFit: 'contain', objectPosition: expandedLogoCentered ? 'center' : 'left center', transform: `translateZ(0) scale(${expandedLogoScale})`, transformOrigin: expandedLogoCentered ? 'center' : 'left center' }}
          loading="eager"
          decoding="sync"
          fetchPriority="high"
          aria-hidden={collapsed}
        />
      )}

      {typeof window !== 'undefined' && (window as any).__TTLOGO_DEBUG_ENABLED && (
        <script dangerouslySetInnerHTML={{ __html: `
          (function(){
            function report(){
              try{
                var e = document.getElementById('tt-logo-expanded');
                var c = document.getElementById('tt-logo-collapsed');
                console.log('TTLogo debug - expanded:', e && (e.complete ? 'complete' : 'loading'), e && e.naturalWidth, 'collapsed:', c && (c.complete ? 'complete' : 'loading'), c && c.naturalWidth);
              }catch(err){console.warn(err)}
            }
            window.addEventListener('load', report);
            var obs = new MutationObserver(report); obs.observe(document.body, {childList:true, subtree:true});
            window.__TTLOGO_DEBUG = report;
          })();
        ` }} />
      )}
    </div>
  );
}
